import React, { useMemo } from 'react'
import styled from 'styled-components'
import { ResponsiveText } from 'components/ResponsiveComponents'
import { Flex, CardBody, Button, AddIcon, IconButton, useMatchBreakpoints } from 'pancakeswap-uikit'
import { Link } from 'react-router-dom'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { Pair } from 'pancakeswap-sdk'
import FullPositionCard from '../../components/PositionCard'
import { useTokenBalancesWithLoadingIndicator } from '../../state/wallet/hooks'
import { usePairs } from '../../hooks/usePairs'
import { toV2LiquidityToken, useTrackedTokenPairs } from '../../state/user/hooks'
import Dots from '../../components/Loader/Dots'
import { AppBody } from '../../components/App'
import Page from '../Page'
import Header from './components/Header'

const Body = styled(CardBody)`
  background: rgba(255, 255, 255, 0.56);
  box-shadow: 0px 106px 168px rgba(0, 0, 0, 0.08), inset 0px 2px 2px rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  padding: 25px 10px;
`

const StyledLink = styled(Link)<{ isMobile? }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '420px')};
`

export default function Liquidity() {
  const { account } = useActiveWeb3React()
  const { t } = useTranslation()
  const { isMobile } = useMatchBreakpoints()

  // fetch the user's balances of all tracked V2 LP tokens
  const trackedTokenPairs = useTrackedTokenPairs()
  const tokenPairsWithLiquidityTokens = useMemo(
    () => trackedTokenPairs.map((tokens) => ({ liquidityToken: toV2LiquidityToken(tokens), tokens })),
    [trackedTokenPairs],
  )
  const liquidityTokens = useMemo(
    () => tokenPairsWithLiquidityTokens.map((tpwlt) => tpwlt.liquidityToken),
    [tokenPairsWithLiquidityTokens],
  )
  const [v2PairsBalances, fetchingV2PairBalances] = useTokenBalancesWithLoadingIndicator(
    account ?? undefined,
    liquidityTokens,
  )

  // fetch the reserves for all V2 pools in which the user has a balance
  const liquidityTokensWithBalances = useMemo(
    () =>
      tokenPairsWithLiquidityTokens.filter(({ liquidityToken }) =>
        v2PairsBalances[liquidityToken.address]?.greaterThan('0'),
      ),
    [tokenPairsWithLiquidityTokens, v2PairsBalances],
  )

  const v2Pairs = usePairs(liquidityTokensWithBalances.map(({ tokens }) => tokens))
  const v2IsLoading =
    fetchingV2PairBalances || v2Pairs?.length < liquidityTokensWithBalances.length || v2Pairs?.some((V2Pair) => !V2Pair)

  const allV2PairsWithLiquidity = v2Pairs.map(([, pair]) => pair).filter((v2Pair): v2Pair is Pair => Boolean(v2Pair))

  const renderBody = () => {
    if (!account) {
      return (
        <ResponsiveText
          textAlign="center"
          fontFamily="Lexend"
          fontWeight="200"
          desktopFontSize="18px"
          mobileFontSize="14px"
          lineHeight="22px"
          color="#1B1B1B"
        >
          {t('Connect to a wallet to view your liquidity.')}
        </ResponsiveText>
      )
    }
    if (v2IsLoading) {
      return (
        <ResponsiveText
          textAlign="center"
          fontFamily="Lexend"
          fontWeight="200"
          desktopFontSize="18px"
          mobileFontSize="14px"
          lineHeight="22px"
          color="#1B1B1B"
        >
          <Dots>{t('Loading')}</Dots>
        </ResponsiveText>
      )
    }
    if (allV2PairsWithLiquidity?.length > 0) {
      return allV2PairsWithLiquidity.map((v2Pair, index) => (
        <FullPositionCard
          key={v2Pair.liquidityToken.address}
          pair={v2Pair}
          mb={index < allV2PairsWithLiquidity.length - 1 ? '16px' : 0}
        />
      ))
    }
    return (
      <ResponsiveText
        textAlign="center"
        fontFamily="Lexend"
        fontWeight="200"
        desktopFontSize="18px"
        mobileFontSize="14px"
        lineHeight="22px"
        color="#1B1B1B"
      >
        {t('No liquidity found.')}
      </ResponsiveText>
    )
  }

  return (
    <>
      <AppBody>
        <Header />
        <Body>
          {renderBody()}
          {account && !v2IsLoading && (
            <Flex flexDirection="column" alignItems="center" mt="24px">
              <ResponsiveText
                fontFamily="Lexend"
                fontWeight="200"
                desktopFontSize="18px"
                mobileFontSize="14px"
                lineHeight="22px"
                color="#1B1B1B"
                mb="8px"
              >
                {t("Don't see a pool you joined?")}
              </ResponsiveText>
              <Button id="import-pool-link" variant="link" as={Link} to="/find" mt="16px">
                {t('Find other LP tokens')}
              </Button>
            </Flex>
          )}
        </Body>
      </AppBody>
      <Flex justifyContent="center" width="100%">
        <StyledLink to="/add" isMobile={isMobile}>
          <IconButton
            id="join-pool-button"
            height="80px"
            endIcon={<AddIcon fill="white" />}
            variant="dark"
            mt="24px"
            width={isMobile ? '100%' : '420px'}
            mb={isMobile ? '72px' : '120px'}
          >
            {t('Add Liquidity')}
          </IconButton>
        </StyledLink>
      </Flex>
    </>
  )
}
